import { alpha, createTheme } from "@mui/material";
import { blue, orange, pink } from "@mui/material/colors";

declare module "@mui/material/styles/createPalette" {
  interface PaletteOptions {
    info?: PaletteColorOptions;
    warning?: PaletteColorOptions;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    stageDirection: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    stageDirection?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    stageDirection: true;
  }
}

const SBColors = {
  orange: {
    primary: "#DF822C", //"#D7813A"
    light: orange[200],
    contrastText: "#252E37"
  },
  purple: {
    primary: "#611549", //"#5B1B48"
    light: pink[500],
    contrastText: "#ffffff"
  }
};

export const Theme = createTheme({
  components: {
    MuiAlert: { styleOverrides: { root: { borderRadius: "10px" } } },
    MuiButton: {
      defaultProps: { color: "primary", variant: "outlined" }
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: "10px" } }
    },
    MuiPaper: {
      defaultProps: { elevation: 3 },
      styleOverrides: { elevation3: { borderRadius: "10px" } }
    },
    MuiTextField: { defaultProps: { size: "small", variant: "outlined" } },
    MuiSelect: { defaultProps: { size: "small" } },
    MuiTableRow: {
      styleOverrides: {
        root: {
          cursor: "pointer",
          "& th": { backgroundColor: SBColors.orange.primary, color: SBColors.orange.contrastText },
          "&$hover:hover": { backgroundColor: alpha(SBColors.purple.primary, 0.2) },
          "&$selected, &$selected:hover": { backgroundColor: alpha(SBColors.purple.primary, 0.4) }
        }
      }
    },
    MuiTabs: { styleOverrides: { indicator: { height: "6px" } } }
  },
  palette: {
    primary: { main: SBColors.orange.primary },
    secondary: { main: SBColors.purple.primary, light: SBColors.purple.light },
    info: { main: blue[500], dark: blue[800] },
    warning: { main: orange[500], light: orange[200] },
    text: { primary: "#252E37" },
    background: { default: "#e9ebef" }
  },
  typography: {
    fontFamily: "Poppins,sans-serif",
    fontSize: 15,
    h6: { fontWeight: "bold", fontSize: 17 },
    body2: { lineHeight: "1.75" },
    stageDirection: {
      fontFamily: "Poppins,sans-serif",
      fontSize: 15,
      lineHeight: "1.75",
      fontStyle: "italic",
      fontWeight: 400,
      letterSpacing: "0.01071em"
    }
  }
});

export const OutboundCallingTheme = createTheme({
  ...Theme,
  components: {
    ...Theme.components,
    MuiTableRow: {
      styleOverrides: {
        root: {
          cursor: "pointer",
          "& th": { backgroundColor: SBColors.purple.primary, color: SBColors.purple.contrastText },
          "&$hover:hover": { backgroundColor: alpha(SBColors.orange.primary, 0.4) },
          "&$selected, &$selected:hover": { backgroundColor: alpha(SBColors.orange.primary, 0.7) }
        }
      }
    }
  },
  palette: {
    ...Theme.palette,
    primary: { main: SBColors.purple.primary, contrastText: SBColors.purple.contrastText },
    secondary: { main: SBColors.orange.primary, light: SBColors.orange.light },
    action: { selected: orange[300] }
  }
});
